import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Field, FormError } from '@/components/form';
import { EmptyState, ErrorState, PageHeader } from '@/components/page';
import { ScopeCheckboxes } from '@/features/clients/scope-checkboxes';
import { scopesQuery } from '@/features/clients/scopes';
import { clientsQuery } from '@/features/layout/client-switcher';
import { StatusPill } from '@/features/register/badges';
import { api, errorMessage } from '@/lib/api';
import { meQuery } from '@/lib/auth';
import { formatDate } from '@/lib/labels';
import type { ClientDetail } from '@/lib/types';

export const Route = createFileRoute('/_app/clients/')({
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQuery);
    if (!me?.canManageClients) throw redirect({ to: '/entries', search: {} });
  },
  component: ClientsPage,
});

function ClientsPage() {
  const clients = useQuery(clientsQuery);
  const [creating, setCreating] = useState(false);

  return (
    <>
      <PageHeader
        title="Clients"
        description="Organisations and systems that use Fraud Safe. Each has its own users, data and API keys."
        actions={
          <Button onClick={() => setCreating(true)}>
            <Plus /> New client
          </Button>
        }
      />
      {clients.isError ? (
        <ErrorState error={clients.error} onRetry={() => void clients.refetch()} />
      ) : (
        <div className="overflow-hidden rounded-xl border bg-card">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted hover:bg-muted">
                <TableHead className="px-4">Client</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Users</TableHead>
                <TableHead className="text-right">Active keys</TableHead>
                <TableHead>Data access</TableHead>
                <TableHead>Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {clients.isPending
                ? Array.from({ length: 3 }, (_, i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={6} className="px-4">
                        <Skeleton className="h-9 w-full" />
                      </TableCell>
                    </TableRow>
                  ))
                : clients.data.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell className="px-4 py-2.5">
                        <Link to="/clients/$clientId" params={{ clientId: c.id }} className="font-medium text-link underline-offset-4 hover:underline">
                          {c.name}
                        </Link>
                        <div className="font-mono text-xs text-muted-foreground">{c.slug}</div>
                      </TableCell>
                      <TableCell>{c.active ? <StatusPill tone="active">Active</StatusPill> : <StatusPill tone="removed">Disabled</StatusPill>}</TableCell>
                      <TableCell className="text-right tabular-nums">{c.userCount}</TableCell>
                      <TableCell className="text-right tabular-nums">{c.activeKeyCount}</TableCell>
                      <TableCell className="text-muted-foreground">{c.readsAllClients ? 'All clients’ entries' : 'Own entries'}</TableCell>
                      <TableCell className="text-muted-foreground">{formatDate(c.createdAt)}</TableCell>
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
          {clients.data && clients.data.length === 0 ? (
            <div className="p-4">
              <EmptyState title="No clients yet">Create a client for each organisation or system that will use Fraud Safe.</EmptyState>
            </div>
          ) : null}
        </div>
      )}
      <CreateClientDialog open={creating} onOpenChange={setCreating} />
    </>
  );
}

function CreateClientDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const scopes = useQuery(scopesQuery);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [allowed, setAllowed] = useState<string[]>(['register:record', 'register:read', 'register:lookup', 'documents:read']);
  const [readsAll, setReadsAll] = useState(false);

  const create = useMutation({
    mutationFn: () => api.post<ClientDetail>('/v1/admin/clients', { name: name.trim(), slug, allowedScopes: allowed, readsAllClients: readsAll }),
    onSuccess: async (c) => {
      toast.success(`Created ${c.name}`);
      await queryClient.invalidateQueries({ queryKey: ['admin', 'clients'] });
      onOpenChange(false);
      await navigate({ to: '/clients/$clientId', params: { clientId: c.id } });
    },
  });

  const autoSlug = (v: string) => v.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 63);

  function submit(e: FormEvent) {
    e.preventDefault();
    create.mutate();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <form onSubmit={submit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>New client</DialogTitle>
            <DialogDescription>You can add API keys and invite its users next.</DialogDescription>
          </DialogHeader>
          <FormError message={create.isError ? errorMessage(create.error) : null} />
          <div className="grid gap-4 sm:grid-cols-2">
            <Field id="client-name" label="Name">
              <Input
                id="client-name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (!slugTouched) setSlug(autoSlug(e.target.value));
                }}
                maxLength={200}
              />
            </Field>
            <Field id="client-slug" label="Slug" hint="The client’s id in API tokens (the iss claim). Can’t be changed later.">
              <Input
                id="client-slug"
                value={slug}
                onChange={(e) => {
                  setSlugTouched(true);
                  setSlug(e.target.value.toLowerCase());
                }}
                className="font-mono"
                maxLength={63}
              />
            </Field>
          </div>
          {scopes.data ? <ScopeCheckboxes all={scopes.data} value={allowed} onChange={setAllowed} /> : <Skeleton className="h-40" />}
          <div className="flex items-start gap-2.5">
            <Checkbox id="reads-all" checked={readsAll} onCheckedChange={(c) => setReadsAll(c === true)} className="mt-0.5" />
            <Label htmlFor="reads-all" className="grid gap-0.5 font-normal">
              <span className="text-sm font-medium">Can see every client’s entries</span>
              <span className="text-xs text-muted-foreground">For a group-wide fraud team. It can still only change its own.</span>
            </Label>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim() || slug.length < 2 || create.isPending}>
              {create.isPending ? 'Creating…' : 'Create client'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
